if (!customElements.get("color-swatch")) {
  class ColorSwatch extends HTMLElement {
    constructor() {
      super();
      this.originalData = null;
      this.lockedData = null;
      this.isLocked = false;
    }

    connectedCallback() {
      this.optionNodes = this.querySelectorAll("[data-value], .color-swatch__item");
      this.init();
    }

    disconnectedCallback() {
      this.optionNodes.forEach((button) => {
        button.removeEventListener("mouseenter", this.onMouseEnter.bind(this));
        button.removeEventListener("click", this.onSwatchClick.bind(this));
      });
      this.removeEventListener("mouseleave", this.onMouseLeave.bind(this));
    }

    init() {
      const productCard = this.closest(".product-card");
      if (!productCard) return;
      
      this.productCard = productCard;
      this.mainImage = productCard.querySelector(
        ".product-card__image--main img"
      );
      
      // 保存原始数据(只保存一次，不再改变)
      this.saveOriginalData();
      
      // 添加悬停事件
      this.optionNodes.forEach((button) => {
        button.addEventListener("mouseenter", this.onMouseEnter.bind(this));
        button.addEventListener("click", this.onSwatchClick.bind(this));
      });

      // 添加鼠标离开事件
      this.addEventListener("mouseleave", this.onMouseLeave.bind(this));

      this.selected = this.querySelector('[aria-selected="true"], .active');

      // 只有当有变体数据时才初始化
      if (this.nextElementSibling && this.nextElementSibling.type === 'application/json') {
        this.getVariantData();
        this.initOptionSwatches();
      }
    }

    saveOriginalData() {
      const titleElement = this.productCard.querySelector('.product-card__title');
      const priceElement = this.productCard.querySelector('.f-price');
      const mainLink = this.productCard.querySelector('.full-width-link');
      const secondImage = this.productCard.querySelector('.product-card__image--second img');
      const secondImageWrapper = this.productCard.querySelector('.product-card__image--second');

      if (!titleElement || !priceElement || !this.mainImage) return;

      this.originalData = {
        image: this.mainImage.src,
        imageSrcset: this.mainImage.srcset,
        imageAlt: this.mainImage.alt,
        imageSecond: secondImage ? secondImage.src : '',
        imageSecondSrcset: secondImage ? secondImage.srcset : '',
        imageSecondHidden: secondImageWrapper ? secondImageWrapper.classList.contains('hidden') : true,
        title: titleElement.textContent.trim(),
        priceHTML: priceElement.innerHTML,
        linkHref: mainLink ? mainLink.href : ''
      };
    }

    initOptionSwatches() {
      this.optionNodes.forEach((optNode) => {
        let variantImage;
        const { optionPosition, value: optionValue } = optNode.dataset;
        const variant = this.variantData.find(
          (v) =>
            v[`option${optionPosition}`] &&
            v[`option${optionPosition}`].toLowerCase() ===
              optionValue.toLowerCase()
        );
        if (variant) {
          variantImage = variant.featured_image
            ? variant.featured_image.src
            : "";
          if (optNode.href) {
            optNode.href = `${optNode.href.split('?')[0]}?variant=${variant.id}`;
          }
          if (variantImage) {
            optNode.setAttribute(
              "data-src",
              getSizedImageUrl(variantImage, "900x")
            );
            optNode.setAttribute("data-srcset", getSrcset(variantImage));
          }
        }
      });
    }

    onSwatchClick(e) {
      const target = e.currentTarget;
      
      // 如果有 data-product-url，说明是元字段关联的产品色板
      if (target.dataset.productUrl) {
        e.preventDefault();
        e.stopPropagation();
        
        // 更新产品信息
        this.updateProductCardFromMetafield(target);
        
        // 锁定状态
        this.isLocked = true;
        
        // 保存锁定时的数据（用于离开时恢复到锁定的状态）
        this.lockedData = {
          url: target.dataset.productUrl,
          title: target.dataset.productTitle,
          price: parseInt(target.dataset.productPrice),
          comparePrice: parseInt(target.dataset.productComparePrice),
          image: target.dataset.productImage,
          imageAlt: target.dataset.productImageAlt,
          imageSecond: target.dataset.productImageSecond,
          available: target.dataset.productAvailable === 'true'
        };
        
        // 更新 active 状态
        this.optionNodes.forEach(node => node.classList.remove('active'));
        target.classList.add('active');
        
        this.selected = target;
      }
    }

    onMouseEnter(e) {
      const { target } = e;
      
      // 如果有 data-product-url，说明是元字段关联的产品色板
      if (target.dataset.productUrl) {
        // 悬停时总是显示对应信息，不受锁定状态影响
        this.updateProductCardFromMetafield(target);
      } else {
        // 原有的变体色板逻辑
        if (!this.isLocked) {
          this.selected && this.selected.removeAttribute("aria-selected");
          target.setAttribute("aria-selected", true);

          const { src, srcset } = target.dataset;
          if (this.mainImage && srcset) {
            this.mainImage.src = src;
            this.mainImage.srcset = srcset;
          }
        }
      }

      this.selected = target;
    }

    onMouseLeave() {
      // 如果已锁定，恢复到锁定的产品信息
      if (this.isLocked && this.lockedData) {
        this.updateProductCardFromData(this.lockedData);
      } else {
        // 未锁定时，恢复到原始产品信息
        this.restoreOriginalData();
      }
    }

    updateProductCardFromMetafield(swatchItem) {
      const productData = {
        url: swatchItem.dataset.productUrl,
        title: swatchItem.dataset.productTitle,
        price: parseInt(swatchItem.dataset.productPrice),
        comparePrice: parseInt(swatchItem.dataset.productComparePrice),
        image: swatchItem.dataset.productImage,
        imageAlt: swatchItem.dataset.productImageAlt,
        imageSecond: swatchItem.dataset.productImageSecond,
        available: swatchItem.dataset.productAvailable === 'true'
      };

      this.updateProductCardFromData(productData);
    }

    updateProductCardFromData(productData) {
      // 更新图片
      if (this.mainImage && productData.image) {
        this.mainImage.src = productData.image;
        this.mainImage.srcset = productData.image;
        this.mainImage.alt = productData.imageAlt || productData.title;
        
        // 更新第二张图片(如果存在)
        const secondImageWrapper = this.productCard.querySelector('.product-card__image--second');
        const secondImage = this.productCard.querySelector('.product-card__image--second img');
        
        if (secondImage && productData.imageSecond) {
          // 有第二张图片,更新它
          secondImage.src = productData.imageSecond;
          secondImage.srcset = productData.imageSecond;
          secondImage.alt = productData.imageAlt || productData.title;
          if (secondImageWrapper) {
            secondImageWrapper.classList.remove('hidden');
          }
        } else if (secondImageWrapper) {
          // 没有第二张图片,隐藏第二张图片容器
          secondImageWrapper.classList.add('hidden');
        }
      }

      // 更新标题
      const titleElement = this.productCard.querySelector('.product-card__title');
      if (titleElement && productData.title) {
        titleElement.textContent = productData.title;
      }

      // 更新价格
      const priceElement = this.productCard.querySelector('.f-price');
      if (priceElement && productData.price) {
        this.updatePrice(priceElement, productData);
      }

      // 更新链接
      const mainLink = this.productCard.querySelector('.full-width-link');
      if (mainLink && productData.url) {
        mainLink.href = productData.url;
      }
    }

    updatePrice(priceElement, data) {
      const isOnSale = data.comparePrice && data.comparePrice > data.price;
      const formattedPrice = this.formatMoney(data.price);
      const formattedComparePrice = this.formatMoney(data.comparePrice);

      if (isOnSale) {
        priceElement.classList.add('f-price--on-sale');
        priceElement.innerHTML = `
          <div class="f-price__sale">
            <span class="f-price-item f-price-item--sale">${formattedPrice}</span>
            <s class="f-price-item f-price-item--regular">${formattedComparePrice}</s>
          </div>
        `;
      } else {
        priceElement.classList.remove('f-price--on-sale');
        priceElement.innerHTML = `
          <span class="f-price__regular f-price-item f-price-item--regular">${formattedPrice}</span>
        `;
      }

      if (!data.available) {
        priceElement.classList.add('f-price--sold-out');
      } else {
        priceElement.classList.remove('f-price--sold-out');
      }
    }

    restoreOriginalData() {
      if (!this.originalData) return;

      // 恢复图片
      if (this.mainImage) {
        this.mainImage.src = this.originalData.image;
        this.mainImage.srcset = this.originalData.imageSrcset || '';
        this.mainImage.alt = this.originalData.imageAlt;
      }

      // 恢复第二张图片(如果存在)
      const secondImage = this.productCard.querySelector('.product-card__image--second img');
      const secondImageWrapper = this.productCard.querySelector('.product-card__image--second');
      
      if (secondImage && this.originalData.imageSecond) {
        secondImage.src = this.originalData.imageSecond;
        secondImage.srcset = this.originalData.imageSecondSrcset || '';
        secondImage.alt = this.originalData.imageAlt;
      }
      
      // 恢复第二张图片的显示/隐藏状态
      if (secondImageWrapper) {
        if (this.originalData.imageSecondHidden) {
          secondImageWrapper.classList.add('hidden');
        } else {
          secondImageWrapper.classList.remove('hidden');
        }
      }

      // 恢复标题
      const titleElement = this.productCard.querySelector('.product-card__title');
      if (titleElement) {
        titleElement.textContent = this.originalData.title;
      }

      // 恢复价格
      const priceElement = this.productCard.querySelector('.f-price');
      if (priceElement) {
        priceElement.innerHTML = this.originalData.priceHTML;
      }

      // 恢复链接
      const mainLink = this.productCard.querySelector('.full-width-link');
      if (mainLink && this.originalData.linkHref) {
        mainLink.href = this.originalData.linkHref;
      }
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    getVariantData() {
      this.variantData =
        this.variantData || JSON.parse(this.nextElementSibling.textContent);
    }
  }
  customElements.define("color-swatch", ColorSwatch);
}
